import React, { useState, useEffect } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from 'react-simple-maps';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Globe, Info } from 'lucide-react';
import { formatCurrencyForOtherScreens, formatNumber } from '../../utils/currencyFormatter';

// World geography data URL - using reliable GeoJSON source
const geoUrl =
  "https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson";

const WorldMap = ({ countryData = [], currency = 'all', selectedCountry, onCountrySelect }) => {
  const [tooltipContent, setTooltipContent] = useState("");
  const [hoveredCountry, setHoveredCountry] = useState(null);

  // Debug logging
  console.log('🗺️ WorldMap Component Received:', {
    countryDataLength: countryData.length,
    countryData: countryData,
    currency: currency
  });

  // Create a map of country codes to data for quick lookup
  const countryDataMap = {};
  countryData.forEach(item => {
    const countryCode = getCountryCode(item.country);
    console.log('🗺️ Mapping country:', {
      originalCountry: item.country,
      mappedTo: countryCode,
      revenue: item.total_revenue,
      transactions: item.total_transactions
    });
    if (countryCode) {
      countryDataMap[countryCode] = item;
    }
  });

  console.log('🗺️ CountryDataMap created:', countryDataMap);
  console.log('🗺️ CountryDataMap keys:', Object.keys(countryDataMap));

  // Get country code from country name (comprehensive mapping)
  function getCountryCode(countryName) {
    const countryMap = {
      // ISO 2-letter to ISO 3-letter mappings
      'US': 'USA',
      'United States': 'USA',
      'CA': 'CAN',
      'Canada': 'CAN',
      'GB': 'GBR',
      'UK': 'GBR',
      'United Kingdom': 'GBR',
      'DE': 'DEU',
      'Germany': 'DEU',
      'FR': 'FRA',
      'France': 'FRA',
      'ES': 'ESP',
      'Spain': 'ESP',
      'IT': 'ITA',
      'Italy': 'ITA',
      'AU': 'AUS',
      'Australia': 'AUS',
      'BR': 'BRA',
      'Brazil': 'BRA',
      'MX': 'MEX',
      'Mexico': 'MEX',
      'CR': 'CRI',
      'Costa Rica': 'CRI',
      'JP': 'JPN',
      'Japan': 'JPN',
      'IN': 'IND',
      'India': 'IND',
      'CN': 'CHN',
      'China': 'CHN',
      // Additional country mappings
      'AR': 'ARG',
      'Argentina': 'ARG',
      'CL': 'CHL',
      'Chile': 'CHL',
      'CO': 'COL',
      'Colombia': 'COL',
      'PE': 'PER',
      'Peru': 'PER',
      'VE': 'VEN',
      'Venezuela': 'VEN',
      'EC': 'ECU',
      'Ecuador': 'ECU',
      'UY': 'URY',
      'Uruguay': 'URY',
      'PY': 'PRY',
      'Paraguay': 'PRY',
      'BO': 'BOL',
      'Bolivia': 'BOL',
      'NL': 'NLD',
      'Netherlands': 'NLD',
      'BE': 'BEL',
      'Belgium': 'BEL',
      'CH': 'CHE',
      'Switzerland': 'CHE',
      'AT': 'AUT',
      'Austria': 'AUT',
      // Central America
      'HN': 'HND',
      'Honduras': 'HND',
      'SV': 'SLV',
      'El Salvador': 'SLV',
      'PA': 'PAN',
      'Panama': 'PAN',
      'SE': 'SWE',
      'Sweden': 'SWE',
      'NO': 'NOR',
      'Norway': 'NOR',
      'DK': 'DNK',
      'Denmark': 'DNK',
      'FI': 'FIN',
      'Finland': 'FIN',
      'PL': 'POL',
      'Poland': 'POL',
      'CZ': 'CZE',
      'Czech Republic': 'CZE',
      'HU': 'HUN',
      'Hungary': 'HUN',
      'RO': 'ROU',
      'Romania': 'ROU',
      'BG': 'BGR',
      'Bulgaria': 'BGR',
      'HR': 'HRV',
      'Croatia': 'HRV',
      'SI': 'SVN',
      'Slovenia': 'SVN',
      'SK': 'SVK',
      'Slovakia': 'SVK',
      'EE': 'EST',
      'Estonia': 'EST',
      'LV': 'LVA',
      'Latvia': 'LVA',
      'LT': 'LTU',
      'Lithuania': 'LTU'
    };
    
    return countryMap[countryName] || countryName.toUpperCase();
  }

  // Get color based on transaction value - fixed to work with any country code
  const getCountryColor = (countryCode, directData = null) => {
    const data = directData || countryDataMap[countryCode];
    if (!data || !data.total_revenue) {
      console.log('🎨 No color data for:', countryCode, data);
      return "#f0f0f0";
    }
    
    // Create color scale based on revenue
    const maxRevenue = Math.max(...countryData.map(d => d.total_revenue || 0));
    if (maxRevenue === 0) return "#f0f0f0";
    
    const intensity = (data.total_revenue / maxRevenue);
    
    console.log('🎨 Color calculated for:', countryCode, {
      revenue: data.total_revenue,
      maxRevenue,
      intensity,
      color: intensity > 0.8 ? "#1e40af" : intensity > 0.6 ? "#3b82f6" : "#60a5fa"
    });
    
    if (intensity > 0.8) return "#1e40af"; // Dark blue
    if (intensity > 0.6) return "#3b82f6"; // Blue
    if (intensity > 0.4) return "#60a5fa"; // Light blue
    if (intensity > 0.2) return "#93c5fd"; // Lighter blue
    if (intensity > 0) return "#dbeafe";   // Very light blue
    
    return "#f0f0f0"; // No data
  };

  // Handle country click
  const handleCountryClick = (geo) => {
    const countryCode = geo.properties.ISO_A3;
    const data = countryDataMap[countryCode];
    if (data && onCountrySelect) {
      onCountrySelect(data.country);
    }
  };

  // Handle country hover
  const handleCountryEnter = (geo) => {
    const countryCode = geo.properties.ISO_A3;
    const data = countryDataMap[countryCode];
    const countryName = geo.properties.NAME;
    
    if (data) {
      const tooltip = `${countryName}\nRevenue: ${formatCurrencyForOtherScreens(data.total_revenue, currency)}\nTransactions: ${formatNumber(data.total_transactions)}`;
      setTooltipContent(tooltip);
    } else {
      setTooltipContent(`${countryName}\nNo transaction data`);
    }
    setHoveredCountry(countryCode);
  };

  const handleCountryLeave = () => {
    setTooltipContent("");
    setHoveredCountry(null);
  };

  // Calculate stats for legend
  const totalRevenue = countryData.reduce((sum, country) => sum + (country.total_revenue || 0), 0);
  const totalTransactions = countryData.reduce((sum, country) => sum + (country.total_transactions || 0), 0);
  const countriesWithData = countryData.filter(c => c.total_revenue > 0).length;

  return (
    <Card className="col-span-full border-0 shadow-xl bg-white">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50/50 border-b border-gray-100">
        <CardTitle className="flex items-center gap-3 text-gray-800 text-xl">
          <div className="p-2 bg-blue-500 rounded-lg text-white">
            <Globe className="h-6 w-6" />
          </div>
          Global Transaction Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Enhanced Stats Summary */}
        <div className="grid grid-cols-3 gap-6 mb-8 p-6 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-xl border border-blue-100">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-800 mb-2">
              {formatCurrencyForOtherScreens(totalRevenue, currency)}
            </div>
            <div className="text-sm font-medium text-gray-700">Total Global Revenue</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-indigo-800 mb-2">
              {formatNumber(totalTransactions)}
            </div>
            <div className="text-sm font-medium text-gray-700">Total Transactions</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-800 mb-2">
              {countriesWithData}
            </div>
            <div className="text-sm font-medium text-gray-700">Active Countries</div>
          </div>
        </div>

        {/* World Map */}
        <div className="relative">
          <ComposableMap
            projection="geoNaturalEarth1"
            projectionConfig={{
              scale: 140,
              center: [0, 0],
            }}
            width={1000}
            height={500}
            style={{ width: "100%", height: "auto", minHeight: "500px" }}
          >
            <ZoomableGroup>
              <Geographies geography={geoUrl}>
                {({ geographies }) =>
                  geographies.map((geo, index) => {
                    // Try multiple possible country code properties
                    const possibleCodes = [
                      geo.properties.ISO_A3,
                      geo.properties.ISO_A2, 
                      geo.properties.ADM0_A3,
                      geo.properties.id,
                      geo.properties.NAME
                    ];
                    
                    // Find which code matches our data
                    let matchedCode = null;
                    let countryData = null;
                    
                    for (let code of possibleCodes) {
                      if (code && countryDataMap[code]) {
                        matchedCode = code;
                        countryData = countryDataMap[code];
                        break;
                      }
                    }
                    
                    // If no direct match, try our mapping function
                    if (!matchedCode) {
                      for (let code of possibleCodes) {
                        if (code) {
                          const mappedCode = getCountryCode(code);
                          if (countryDataMap[mappedCode]) {
                            matchedCode = mappedCode;
                            countryData = countryDataMap[mappedCode];
                            break;
                          }
                        }
                      }
                    }
                    
                    // Debug logging for first few countries
                    if (index < 10 || matchedCode) {
                      console.log('🗺️ Country Match Debug:', {
                        index,
                        geoProperties: geo.properties,
                        possibleCodes,
                        matchedCode,
                        hasData: !!countryData,
                        ourDataKeys: Object.keys(countryDataMap)
                      });
                    }
                    
                    const countryCode = matchedCode || geo.properties.ISO_A3;
                    const isSelected = selectedCountry && getCountryCode(selectedCountry) === countryCode;
                    const hasData = !!countryData;
                    
                    // Debug logging for a few key countries  
                    if (countryCode === 'USA' || countryCode === 'CRI' || countryCode === 'HND' || countryCode === 'COL') {
                      console.log('🗺️ Geography Country Debug:', {
                        geoCountryCode: countryCode,
                        geoCountryName: geo.properties.NAME,
                        hasData: hasData,
                        dataInMap: countryDataMap[countryCode],
                        color: getCountryColor(countryCode)
                      });
                    }
                    
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onMouseEnter={() => handleCountryEnter(geo)}
                        onMouseLeave={handleCountryLeave}
                        onClick={() => handleCountryClick(geo)}
                        style={{
                          default: {
                            // Hardcode test: color USA and Canada blue to test if coloring works at all
                            fill: (geo.properties.NAME === "United States of America" || geo.properties.NAME === "Canada") ? "#1e40af" : "#f0f0f0",
                            outline: "none",
                            stroke: "#ffffff", 
                            strokeWidth: 0.5,
                          },
                          hover: {
                            fill: hasData ? "#1d4ed8" : "#d1d5db",
                            outline: "none",
                            stroke: "#ffffff",
                            strokeWidth: 0.5,
                            cursor: hasData ? "pointer" : "default",
                          },
                          pressed: {
                            fill: "#1e3a8a",
                            outline: "none",
                            stroke: "#ffffff",
                            strokeWidth: 0.5,
                          },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
            </ZoomableGroup>
          </ComposableMap>

          {/* Enhanced Tooltip */}
          {tooltipContent && (
            <div className="absolute top-6 left-6 bg-white p-4 rounded-xl shadow-2xl border border-gray-200 max-w-xs z-50">
              <div className="flex items-start gap-3">
                <div className="p-1 bg-blue-100 rounded-lg">
                  <Info className="h-4 w-4 text-blue-600" />
                </div>
                <div className="text-sm">
                  {tooltipContent.split('\n').map((line, i) => (
                    <div key={i} className={i === 0 ? "font-semibold text-gray-900 mb-1" : "text-gray-600"}>
                      {line}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Color Scale Legend */}
        <div className="mt-4 flex items-center justify-center">
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Low Activity</span>
            <div className="flex space-x-1">
              <div className="w-4 h-4 bg-gray-200 rounded"></div>
              <div className="w-4 h-4 bg-blue-100 rounded"></div>
              <div className="w-4 h-4 bg-blue-200 rounded"></div>
              <div className="w-4 h-4 bg-blue-400 rounded"></div>
              <div className="w-4 h-4 bg-blue-600 rounded"></div>
              <div className="w-4 h-4 bg-blue-800 rounded"></div>
            </div>
            <span>High Activity</span>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-4 text-center text-sm text-gray-500">
          Click on a country to filter analytics data • Hover to see detailed information
        </div>
      </CardContent>
    </Card>
  );
};

export default WorldMap;